import { defineComponent, h, PropType } from 'vue';
import { into } from '@vaaas/fpts/object';
import { State } from '../state';
import { Settings } from '../Settings';

import ColorsMaterials from './Modals/ColorsMaterials';
import DebugVars from './Modals/DebugVars';
import DoMagic from './Modals/DoMagic';
import DrawExactCircle from './Modals/DrawExactCircle';
import Export from './Modals/Export';
import ExportToOnshape from './Modals/ExportToOnshape';
import { FloatingToolbar } from '@features/floating-toolbar';
import GenericHelp from './Modals/GenericHelp';
import ImportRefSTLModel from './Modals/ImportRefSTLModel';
import ProceedWithClearSymmetry from './Modals/ProceedWithClearSymmetry';
import ProceedWithImport from './Modals/ProceedWithImport';
import { ReadCoordinatesModal } from '@features/read-coordinates';
import ReadSTLResizeValue from './Modals/ReadSTLResizeValue';
import ReadValue from './Modals/ReadValue';
import Refresh from './Modals/Refresh';
import SavePhiModel from './Modals/SavePhiModel';
import { ScaleModal } from '@phi/scale';
import { SearchModal } from '@phi/search';
import SelectModel from './Modals/SelectModel';
import SetGridSize from './Modals/SetGridSize';
import SetProjectEdgesOffset from './Modals/SetProjectEdgesOffset';
import SetVertexXYZ from './Modals/SetVertexXYZ';
import BooleanOps from './Modals/BooleanOps';
import { LoadReferenceSketchModal } from '@phi/saving-loading/reference-sketches';
import { ViewPortManager } from '@features/save-viewpoint';
import VertexDistance from './Modals/VertexDistance';
import Welcome from './Modals/Welcome';
import WelcomeDemo from './Modals/WelcomeDemo';
import { uid_deselect_all_objects } from '../ui_display';
import ImportTroubleshooting from './Modals/ImportTroubleshooting';
import { ImportRefPartstudioModal } from '@features/references';
import BooleanDialog from './Modals/BooleanDialog';
import { ColorPicker } from '@features/color-picker';
import { SettingsModal } from '@features/user-settings';
import { SmoothenOptionsModal } from '@features/smoothness';
import { CurvatureCombsOptionsModal } from '@features/curvature-combs';
import { MustLoginModal } from '@phi/authentication/frontend';
import { IFrontendServiceContainer } from '@phi/frontend/service-container';
import { ProFeatureModal, ViewOnlyModal } from '@phi/subscriptions/frontend';
import { CookieConsentModal, CookieConsentProvider, ICookieConsentProvisions } from '@phi/analytics';

export default defineComponent({
    props: {
        state: {
            required: true,
            type: Object as PropType<State>,
        },
        settings: {
            required: true,
            type: Object as PropType<Settings>,
        },
        app: {
            required: true,
            type: Object as PropType<IFrontendServiceContainer>,
        },
    },

    render() {
        const modal = into(this.state.modal);
        return h('div', { id: 'modals' }, [
            h(ColorsMaterials, {
                show: this.state.modal.materialsModal,
                'onUpdate:show': (x) => {
                    this.state.modal.materialsModal = x;
                    uid_deselect_all_objects();
                },
            }),

            h(DebugVars, {
                show: this.state.modal.DebugVarsModal,
                'onUpdate:show': modal('DebugVarsModal'),
            }),

            h(DoMagic, {
                show: this.state.modal.doMagicModal,
                'onUpdate:show': modal('doMagicModal'),
            }),

            h(DrawExactCircle, {
                show: this.state.modal.drawExactCircle,
                'onUpdate:show': modal('drawExactCircle'),
            }),

            h(Export, {
                show: this.state.modal.ExportModal,
                'onUpdate:show': modal('ExportModal'),
                current_filename: this.state.current_filename,
            }),

            h(ExportToOnshape, {
                show: this.state.modal.ExportToOnshapeModal,
                'onUpdate:show': modal('ExportToOnshapeModal'),
                default_name: this.state.current_filename,
                state: this.state,
            }),

            h(FloatingToolbar),

            h(GenericHelp, {
                show: this.state.modal.generic_help_modal,
                'onUpdate:show': modal('generic_help_modal'),
            }),

            h(ImportRefSTLModel, {
                state: this.state,
                show: this.state.modal.ImportRefStlModelModal,
                'onUpdate:show': modal('ImportRefStlModelModal'),
            }),

            h(ProceedWithClearSymmetry, {
                show: this.state.modal.proceedWithClearSymmetryModal,
                'onUpdate:show': modal('proceedWithClearSymmetryModal'),
            }),

            h(ProceedWithImport, {
                show: this.state.modal.proceedWithImportModal,
                'onUpdate:show': modal('proceedWithImportModal'),
            }),

            h(ReadCoordinatesModal, { units: this.state.display_units }),

            h(ReadSTLResizeValue, {
                show: this.state.modal.readSTLResizeValueModal,
                'onUpdate:show': modal('readSTLResizeValueModal'),
            }),

            h(ReadValue, {
                show: this.state.modal.readValueModal,
                display_units: this.state.display_units,
                'onUpdate:show': modal('readValueModal'),
            }),

            h(Refresh, {
                show: this.state.modal.refreshModal,
                'onUpdate:show': modal('refreshModal'),
            }),

            h(SavePhiModel, { app: this.app }),

            h(ScaleModal, {
                show: this.state.modal.scaleModal,
                'onUpdate:show': modal('scaleModal'),
            }),

            h(SearchModal, {
                show: this.state.modal.searchModal,
                'onUpdate:show': modal('searchModal'),
            }),

            h(SelectModel, {
                framework: this.state.framework,
                show: this.state.modal.selectModelModal,
                'onUpdate:show': modal('selectModelModal'),
                units: this.state.display_units,
                state: this.state,
                session_type: this.app.authentication.session_type.value,
            }),

            h(SetGridSize, {
                show: this.state.modal.setGridsizeModal,
                'onUpdate:show': modal('setGridsizeModal'),
                units: this.state.display_units,
            }),

            h(SetProjectEdgesOffset, {
                show: this.state.modal.projectEdgesOffset,
                'onUpdate:show': modal('projectEdgesOffset'),
            }),

            h(SetVertexXYZ, {
                show: this.state.modal.setVertexXYZModal,
                'onUpdate:show': modal('setVertexXYZModal'),
                selected_vertices_count: this.state.selected_vertices_count,
                app: this.app,
            }),

            h(BooleanOps, {
                show: this.state.modal.booleanOpsModal,
                'onUpdate:show': modal('booleanOpsModal'),
                selected_vertices_count: this.state.selected_vertices_count,
                app: this.app,
            }),

            h(LoadReferenceSketchModal, {
                show: this.state.modal.sketchImagesModal,
                'onUpdate:show': modal('sketchImagesModal'),
                app: this.app,
            }),

            h(ViewPortManager),

            h(VertexDistance, {
                show: this.state.modal.vertexDistanceModal,
                'onUpdate:show': modal('vertexDistanceModal'),
            }),

            h(Welcome, {
                show: this.state.modal.tutorialsModal,
                'onUpdate:show': modal('tutorialsModal'),
            }),

            h(WelcomeDemo, {
                show: this.state.modal.tutorialsModalDemo,
                'onUpdate:show': modal('tutorialsModalDemo'),
            }),

            h(ImportTroubleshooting, {
                show: this.state.modal.importTroubleshootingModal,
                'onUpdate:show': modal('importTroubleshootingModal'),
            }),

            h(ImportRefPartstudioModal, { state: this.state }),

            h(BooleanDialog, {
                show: this.state.modal.booleanDialog,
                'onUpdate:show': modal('booleanDialog'),
            }),

            h(ColorPicker),

            h(SettingsModal, { app: this.app }),

            h(MustLoginModal, { auth: this.app.authentication }),

            h(SmoothenOptionsModal),

            h(CurvatureCombsOptionsModal),

            h(ProFeatureModal, { subscriptions: this.app.subscriptions }),

            h(ViewOnlyModal, { subscriptions: this.app.subscriptions }),

            h(CookieConsentProvider, { analytics: this.app.analytics }, {
                default: (provisions: ICookieConsentProvisions) =>
                    h(CookieConsentModal, {
                        shouldAsk: provisions.shouldAsk.value,
                        'onUpdate:consent': provisions.updateConsent,
                    })
            }),
        ]);
    },
});
